import React, { CSSProperties } from 'react';
import { Tooltip } from 'antd';
import {
  LockOutlined,
  FolderOpenOutlined,
  MoreOutlined,
  EditOutlined,
  PlusSquareOutlined
} from '@ant-design/icons';
import { useTranslation } from 'react-i18next';
import { useSortable } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import DeleteNodeConfirmModal from '../../tree/DeleteNodeConfirmModal';
import SiteMapModel from '../../../steps/Sitemap/models/SiteMapModel';
import ITreeNode from '../../tree/ITreeNode';

interface InternalTreeNodeProps {
  node: ITreeNode;
  depth: number;
  indentationWidth: number;
  ghost?: boolean;
  indicator?: boolean;
  isIndexPage?: boolean;
  isDefaultPage?: boolean;
  showFolderIcon?: boolean;
  allNodes?: ITreeNode[];
  projected?: { depth: number; parentId: string | number | null } | null;
  activeId?: string | number | null;
}

const InternalTreeNode: React.FC<InternalTreeNodeProps> = ({
  node,
  depth,
  indentationWidth = 50,
  ghost = false,
  indicator = false,
  isIndexPage = false,
  isDefaultPage = false,
  showFolderIcon = false,
  allNodes,
  projected = null,
  activeId = null
}) => {
  const { t } = useTranslation();
  const [isHovered, setIsHovered] = React.useState(false);

  // Keep default and index pages locked for edit/delete
  const isLocked = isDefaultPage || isIndexPage;

  // Pages that cannot be moved: default and index
  const draggable = !(isDefaultPage || isIndexPage);

  const { 
    attributes, 
    listeners, 
    setNodeRef,
    transform,
    transition,
    isDragging,
    isOver
  } = useSortable({
    id: node.id,
    disabled: !draggable,
    data: {
      node: node,
      type: 'internal',
      isDefaultPage: isDefaultPage,
      isIndexPage: isIndexPage,
      canAcceptDrop: !isDefaultPage && !isIndexPage
    }
  });

  const hasChildren = SiteMapModel.hasChildren(node);
  let isParent = hasChildren;
  if (activeId && projected && projected.parentId === node.id) {
    isParent = true;
  }

  const colorClass =
    isParent
      ? 'text-[rgba(0,0,0,0.65)]'
      : 'text-[var(--colorText, rgba(0, 0, 0, 0.88))]';

  // Calculate padding based on depth and type
  let calculatedPadding: string;
  if (isIndexPage) {
    calculatedPadding = '20px 24px 20px 24px';
  } else if (depth === 0 && isParent) {
    calculatedPadding = '28px 24px 4px 24px';
  } else if (depth === 0) {
    calculatedPadding = '20px 24px 16px 24px';
  } else {
    const indent = 15 + depth * indentationWidth;
    calculatedPadding = `20px 24px 16px ${indent}px`;
  }

  // Styling for drag state
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition: transition || 'background-color 0.2s ease',
    borderTop: isIndexPage
      ? '1px solid var(--colorBorder, rgba(0, 0, 0, 0.15))'
      : undefined,
    borderBottom: '1px solid var(--colorBorder, rgba(0, 0, 0, 0.15))',
    padding: calculatedPadding,
    cursor: draggable ? (isDragging ? 'grabbing' : 'grab') : 'default',
    position: 'relative',
  };

  // Background color logic
  let backgroundColor = 'transparent';
  if (isDragging) {
    backgroundColor = '#F5F5F5'; // Light gray for drop zone
  } else if (ghost) {
    backgroundColor = '#E6F7FF';
  } else if (isHovered && draggable) {
    backgroundColor = '#E6F7FF'; // Blue hover state for draggable items
  }

  style.backgroundColor = backgroundColor;

  return (
    <div
      ref={setNodeRef}
      {...(draggable ? attributes : {})}
      {...(draggable ? listeners : {})}
      style={style}
      onMouseEnter={() => draggable && setIsHovered(true)}
      onMouseLeave={() => setIsHovered(false)}
    >
      {isDragging ? (
        // Drop zone: light gray, no text or icons, visually larger
        <div style={{ width: '100%', minHeight: '20px', backgroundColor: '#F5F5F5', borderRadius: '6px' }} />
      ) : (
        <>
          <div className={`flex items-center gap-2 text-[16px] relative leading-[22px] ${colorClass} transition-colors`}>
            {(isParent || showFolderIcon) && (
              <FolderOpenOutlined
                className="w-4 h-4 mr-1"
                style={{
                  color: 'var(--colorTextLabel, rgba(0, 0, 0, 0.65))',
                }}
              />
            )}
            {!isParent && depth > 0 && <MoreOutlined className="w-4 h-4 mr-1" />}

            {node.text}

            {isLocked ? (
              <Tooltip title={t('This page is locked and cannot be moved or edited')}>
                <LockOutlined
                  className="ml-auto"
                  style={{ fontSize: '16px', color: 'rgba(0, 0, 0, 0.45)' }}
                />
              </Tooltip>
            ) : (
              <div 
                className="ml-auto flex gap-2"
                onPointerDown={(e) => e.stopPropagation()}
              >
                <EditOutlined
                  onClick={() => SiteMapModel.openEditPageModal(node)}
                  className="cursor-pointer"
                  style={{
                    fontSize: '16px',
                    color: 'rgba(0, 0, 0, 0.45)',
                  }}
                />
                <DeleteNodeConfirmModal
                  node={node}
                  onAction={(action, data) => {
                    if (action === 'delete') SiteMapModel.deleteNode(data.node);
                    if (action === 'delete-and-move-subpages')
                      SiteMapModel.deleteNodeAndMoveSubpages(data.node);
                  }}
                  hasChildren={hasChildren}
                />
              </div>
            )}

          </div>

          {/* Show + for any non-default page (Startseite will show +) - hide during drag and in ghost mode */}
          {!isDefaultPage && !isDragging && !ghost && (
            <Tooltip
              title={t('Create a new page below.')}
              placement="top"
              overlayClassName="max-w-[320px]"
              overlayStyle={{ minWidth: 180 }}
            >
              <div
                className="absolute bottom-[-10px] left-[50%] transform translate-x-[-50%] cursor-pointer bg-white z-10 px-1"
                title="Neue Seite daneben erstellen."
                onClick={() => SiteMapModel.openAddPageModalAsSibling(node)}
                onPointerDown={(e) => e.stopPropagation()}
              >
                <PlusSquareOutlined
                  style={{ fontSize: '16px' }}
                  className="text-gray-600"
                />
              </div>
            </Tooltip>
          )}

          {/* Render children when dragging */}
          {isDragging && allNodes && (() => {
            // Do not render children in drop zone
            return null;
          })()}
        </>
      )}
    </div>
  );
};

export default InternalTreeNode;
